import { routePaths } from "../../app/navigation";
import type { AccessRight } from "../../shared/auth/accessRights";
import { hasAccessRight } from "../../shared/auth/accessRights";

/**
 * Areas of „Mandant“, each shown on its own below a sub-navigation instead of one long page
 * (UX-016): company and settings, special regimes, rule overrides and legal deviations, roles and
 * access, support access, and the contract with its documents and the data export. Six areas, like
 * „Verwaltung“, keep the sub-navigation at three rows on phones.
 */
export const tenantSections = [
  "general",
  "regimes",
  "rules",
  "access",
  "support",
  "contract",
] as const;

/** An area of „Mandant“. */
export type TenantSection = (typeof tenantSections)[number];

/**
 * Access rights of which one shows an area: HR administrators maintain the leave rules under
 * „Allgemein“ (T-161) and request the complete export under „Vertrag und Daten“ (T-138); every other
 * area needs the tenant administration.
 */
export const tenantSectionRights: Readonly<Record<TenantSection, readonly AccessRight[]>> = {
  general: ["ManageTenant", "ManageMasterData"],
  regimes: ["ManageTenant"],
  rules: ["ManageTenant"],
  access: ["ManageTenant"],
  support: ["ManageTenant"],
  contract: ["DecideLegalDocuments", "ExportTenantData"],
};

/**
 * Returns the areas the roles may see, in display order.
 *
 * @param roles - Roles of the person.
 * @returns The areas.
 */
export function visibleTenantSections(roles: readonly string[]): readonly TenantSection[] {
  const visible = tenantSections.filter((section) =>
    tenantSectionRights[section].some((right) => hasAccessRight(roles, right)),
  );

  return visible;
}

/** Name of the search parameter that selects the area, for example `/tenant?section=rules`. */
export const tenantSectionParameter = "section";

/**
 * Reads the area from the search parameter; a missing, unknown or hidden value selects the first
 * visible area.
 *
 * @param value - Value of the search parameter.
 * @param visible - The areas the person may see; all by default.
 * @returns The area.
 */
export function readTenantSection(
  value: string | null,
  visible: readonly TenantSection[] = tenantSections,
): TenantSection {
  const section = visible.find((item) => item === value) ?? visible[0] ?? "general";

  return section;
}

/**
 * Returns the path of an area, for links from other screens (for example the legal prompt or the
 * export hint of the service status).
 *
 * @param section - The area.
 * @returns The path; the first area has no search parameter.
 */
export function tenantSectionPath(section: TenantSection): string {
  const path =
    section === "general"
      ? routePaths.tenant
      : `${routePaths.tenant}?${tenantSectionParameter}=${section}`;

  return path;
}
